import { OrderDetails } from '@/models/order-details/order-details';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { ConfigService } from '@services/config.service';
import { TimeTrackingDetails } from '@/models/time-tracking/time-tracking-details';

@Component({
  selector: 'app-dispatch-management',
  templateUrl: './dispatch-management.component.html',
  styleUrl: './dispatch-management.component.scss'
})
export class DispatchManagementComponent implements OnInit {
  orders: OrderDetails[] = [];
  selectedStatus = 'PROCESSING';
  selectedOrder: any;
  orderItems: any[] = [];
  statusFilter: string = 'ALL';
  shippingFilter: string = 'ALL';
  isItemsModalOpen = false;
  searchText: any;
  endPoint: string = "";
  allOrders: OrderDetails[] = [];
  statusList: string[] = [];
  shippingPartnerList: string[] = [];
  private secretKey = "#EVOL-&$%$DF%SION*^%#^*F&%^DFSEYUJ";
  timeTrackingList: TimeTrackingDetails[] = [];
  selectedTrackingOrder: any = null;
  isTimeTrackingModalOpen = false;
  isTimeTrackingLoading = false;

  constructor(
    private spinner: NgxSpinnerService,
    private appService: AppService,
    private toastr: ToastrService,
    private router: Router,
    private config: ConfigService
  ) { }

  ngOnInit(): void {
    this.loadOrdersByStatus();
    this.endPoint = this.config.getConfig().END_POINT;
  }

  /* LOAD ORDERS */
  loadOrdersByStatus() {
    this.spinner.show();

    forkJoin([
      this.appService.getByStatusOrderDetails('PROCESSING'),
      this.appService.getByStatusOrderDetails('PACKED'),
      // this.appService.getByStatusOrderDetails('SHIPPED'),
    ]).subscribe({
      next: ([processing, packed]) => {

        const orderDetails = [...(processing || []), ...(packed || [])];

        this.allOrders = orderDetails.sort((a, b) => b.orderId - a.orderId);

        this.allOrders.forEach((order: any) => {
          order.shippingAmountReadonly = Number(order.shippingAmount) > 0;
        });

        // Extract filters again
        this.statusList = [...new Set(
          this.allOrders.map(o => o.orderStatus).filter(s => !!s)
        )];

        this.shippingPartnerList = [...new Set(
          this.allOrders.map(o => o.shippingPartner).filter(s => !!s)
        )];

        // ✅ IMPORTANT: Re-apply filter here
        this.applyFilters();

        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Failed to load orders');
      }
    });
  }

  applyFilters() {

    this.orders = this.allOrders.filter(order => {

      const matchesSearch =
        !this.searchText ||
        order.invoiceNumber?.toLowerCase().includes(this.searchText.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(this.searchText.toLowerCase()) ||
        order.phoneNumber?.toLowerCase().includes(this.searchText.toLowerCase());

      const matchesStatus =
        this.statusFilter === 'ALL' ||
        order.orderStatus === this.statusFilter;

      const matchesShipping =
        this.shippingFilter === 'ALL' ||
        order.shippingPartner === this.shippingFilter;

      return matchesSearch && matchesStatus && matchesShipping;

    });

  }

  resetFilters() {
    this.searchText = '';
    this.statusFilter = 'ALL';
    this.shippingFilter = 'ALL';
    this.orders = [...this.allOrders];
  }

  /* OPEN MODAL */
  openItemsModal(order: any) {
    this.selectedOrder = order;
    this.orderItems = order.orderItems || [];
    this.orderItems.forEach(item => {
      if (item.productVariantId) {
        this.variantIdBasedImageDetailsGetting(item);
      }
    });
    this.isItemsModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  variantIdBasedImageDetailsGetting(item: any) {
    this.spinner.show();
    this.appService.variantIdBasedImageDetailsGetting(item.productVariantId).subscribe({
      next: (res: any) => {
        this.spinner.hide();

        // store imageId inside that row
        item.imageDetailsId = res.imageDetailsId;
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  getImageById(imageId: number): string {
    return imageId
      ? `${this.endPoint}/api/product/image-details/get/${imageId}`
      : 'assets/img/my-1.jpg';
  }


  /* CLOSE MODAL */
  closeItemsModal() {
    this.isItemsModalOpen = false;
    document.body.style.overflow = 'auto';
  }

  /* CHANGE ITEM STATUS */
  changeItemStatus(item: any, status: string) {

    const payload = {
      orderItemId: item.orderItemId,
      orderStatus: status
    };

    this.spinner.show();

    this.appService.updateOrderItemStatus(payload).subscribe({
      next: () => {
        item.orderStatus = status;
        this.updateOrderStatusLocally();
        this.spinner.hide();
        this.toastr.success('Item Updated');
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Failed to update item');
      }
    });
  }

  openTimeTrackingModal(order: any): void {
    if (!order?.orderId) {
      this.toastr.error('Order ID not found');
      return;
    }
    this.selectedTrackingOrder = order;
    this.timeTrackingList = [];
    this.isTimeTrackingModalOpen = true;
    document.body.style.overflow = 'hidden';
    this.isTimeTrackingLoading = true;
    this.appService.getTimeTrackingByOrderId(order.orderId).subscribe({
      next: (res: TimeTrackingDetails[]) => {
        this.isTimeTrackingLoading = false;
        this.timeTrackingList = res || [];
        // Optional: latest tracking first
        this.timeTrackingList.sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
        );
      },
      error: (err) => {
        this.isTimeTrackingLoading = false;
        this.timeTrackingList = [];
        this.toastr.error(
          err?.error?.message || 'Failed to load time tracking'
        );

      }

    });

  }

  closeTimeTrackingModal(): void {
    this.isTimeTrackingModalOpen = false;
    this.selectedTrackingOrder = null;
    this.timeTrackingList = [];
    document.body.style.overflow = 'auto';
  }

  formatTrackingDate(timestamp: number): string {

  if (!timestamp) {
    return '-';
  }

  const date = new Date(timestamp < 10000000000
    ? timestamp * 1000
    : timestamp
  );

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

}

  getTimeTrackingStatusClass(status: string): string {

    switch (status) {

      case 'PLACED':
        return 'bg-warning text-dark';

      case 'PROCESSING':
        return 'bg-info text-dark';

      case 'PACKED':
        return 'bg-primary';

      case 'SHIPPED':
        return 'bg-success';

      case 'DELIVERED':
        return 'bg-success';

      case 'CANCELLED':
        return 'bg-danger';

      case 'RETURNED':
        return 'bg-secondary';

      default:
        return 'bg-dark';
    }

  }

  /* CHECK ALL ITEMS PACKED */
  isOrderPacked(order: any): boolean {
    if (!order.orderItems) return false;
    return order.orderItems.every((i: any) => i.orderStatus === 'PACKED');
  }


  /* UPDATE ORDER STATUS LOCALLY */
  updateOrderStatusLocally() {
    if (this.isOrderPacked(this.selectedOrder)) {
      this.selectedOrder.orderStatus = 'PACKED';
    } else {
      this.selectedOrder.orderStatus = 'PROCESSING';
    }
  }

  /* UPDATE SHIPPING */
  updateShipping(order: any) {

    if (!this.isOrderPacked(order)) {
      this.toastr.error('All items must be PACKED');
      return;
    }

    if (!order.trackingNumber || order.trackingNumber.trim() === '') {
      this.toastr.error('Tracking ID is required');
      return;
    }

    // if (Number(order.shippingAmount) <= 0) {
    //   this.toastr.error('Please enter Shipping Amount');
    //   return;
    // }

    const payload = {
      orderId: order.orderId,
      trackingNumber: order.trackingNumber,
      orderStatus: 'SHIPPED',
      shippingPartner: order.shippingPartner,
      weight: order.weight,
      actualShippingAmount: Number(order.shippingAmount)
    };

    this.spinner.show();

    this.appService.updateBulkOrderItems(payload).subscribe({
      next: () => {
        order.orderStatus = 'SHIPPED';
        order.shippingUpdated = true;
        this.spinner.hide();
        this.toastr.success('Order Shipped Successfully');
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Shipping update failed');
      }
    });
  }

  updateFullOrderStatus(status: string) {

    if (!this.selectedOrder) {
      this.toastr.error('No order selected');
      return;
    }

    const payload = {
      orderId: this.selectedOrder.orderId,
      orderStatus: status
    };

    this.spinner.show();

    this.appService.updateBulkOrderItems(payload).subscribe({
      next: () => {

        /* Update UI Locally */
        this.orderItems.forEach(item => {
          item.orderStatus = status;
        });

        this.selectedOrder.orderStatus = status;

        this.spinner.hide();
        this.toastr.success(`Order marked as ${status}`);

        /* Refresh main table */
        this.loadOrdersByStatus();

        this.closeItemsModal();
      },

      error: (err) => {
        this.spinner.hide();
        this.toastr.error(err?.error?.message || 'Status update failed');
      }
    });
  }

  /* PRINT BILL */
  printBill(order: OrderDetails) {
    const encryptedBillId = CryptoJS.AES.encrypt(String(order.billId), this.secretKey).toString();
    const encryptedBuyerType = CryptoJS.AES.encrypt(String(order.buyerType), this.secretKey).toString();
    const encryptedScreenName = CryptoJS.AES.encrypt(String("dispatch-management"), this.secretKey).toString();
    this.router.navigate(['/bill'], { queryParams: { billId: encryptedBillId, buyerType: encryptedBuyerType, screenName: encryptedScreenName } });
  }

  openImage(imageId: number): void {

    const imageUrl = this.getImageById(imageId);

    const newWindow = window.open('', '_blank');

    if (newWindow) {

      newWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
          <title>Image Preview</title>

          <style>

              html,body{
                  margin:0;
                  padding:0;
                  width:100%;
                  height:100%;
                  background:#111;
                  display:flex;
                  justify-content:center;
                  align-items:center;
                  overflow:hidden;
              }

              img{
                  max-width:100%;
                  max-height:100%;
                  object-fit:contain;
                  user-select:none;
              }

          </style>

      </head>

      <body>

          <img src="${imageUrl}" />

      </body>

      </html>
    `);

      newWindow.document.close();

    }

  }


}

